import Vue from "vue";

import { parsePhoneNumber } from "libphonenumber-js";

export default function (_ctx, inject) {
  function formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) {
      return "";
    }

    const { number, countryCode } = phoneNumber;

    if (!number) {
      return "";
    }
    const iso = countryCode?.iso || "HU";

    try {
      const parsedNumber = parsePhoneNumber(number, iso.trim().toUpperCase());
      return parsedNumber.formatInternational({
        formatExtension(number, extension) {
          return `${number} / ${extension}`;
        },
      });
    } catch {
      return `+${countryCode?.code}${number}`;
    }
  }

  function getPhoneNumberLink(phoneNumber) {
    if (!phoneNumber) {
      return "";
    }

    const { number, countryCode } = phoneNumber;

    if (!number) {
      return "";
    }
    const iso = countryCode?.iso || "HU";

    try {
      const parsedNumber = parsePhoneNumber(number, iso.trim().toUpperCase());
      const formattedNumber = parsedNumber.formatInternational({
        formatExtension(number, extension) {
          return `${number},${extension}`;
        },
      });

      return `tel:${formattedNumber.replace(/ /g, "")}`;
    } catch {
      return "";
    }
  }

  const support = {
    email: process.env.SUPPORT_MAIL,
    phone: formatPhoneNumber({
      number: process.env.SUPPORT_PHONE,
      countryCode: process.env.SUPPORT_PHONE_COUNTRY,
    }),
    emailLink: `mailto:${process.env.SUPPORT_MAIL}`,
    phoneLink: getPhoneNumberLink({
      number: process.env.SUPPORT_PHONE,
      countryCode: process.env.SUPPORT_PHONE_COUNTRY,
    }),
  };

  const helpers = {
    topDomain: process.env.TOP_DOMAIN,
    phpDomain: process.env.TOP_DOMAIN,
    seoSubdomain: process.env.SEO_SUBDOMAIN,
    cioWorkspaceId: process.env.CIO_WORKSPACE_ID,
    support,
    socialNetworkUrls: [
      {
        type: "facebook",
        label: "Facebook",
        url: process.env.FACEBOOK_PAGE_URL,
        icon: "facebook-f",
      },
      {
        type: "instagram",
        label: "Instagram",
        url: process.env.INSTAGRAM_PAGE_URL,
        icon: "instagram",
      },
      {
        type: "pinterest",
        label: "Pinterest",
        url: process.env.PINTEREST_PAGE_URL,
        icon: "pinterest",
      },
      {
        type: "twitter",
        label: "X (Twitter)",
        url: process.env.TWITTER_PAGE_URL,
        icon: "twitter",
      },
      {
        type: "tiktok",
        label: "TikTok",
        url: process.env.TIKTOK_PAGE_URL,
        icon: "tiktok",
      },
    ].filter((network) => network.url),
    addSearchParamsToUrl(url, params) {
      const urlSearchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          urlSearchParams.append(key, value);
        }
      });

      return new URL(`${url}?${urlSearchParams}`).href;
    },
    getLeadToWorkEmailLogUrl(email) {
      if (
        !process.env.LEADTOWORK_URL ||
        !process.env.LEADTOWORK_BRAND_ID ||
        !email
      ) {
        return null;
      }

      return this.addSearchParamsToUrl(
        `${process.env.LEADTOWORK_URL}/brand/${process.env.LEADTOWORK_BRAND_ID}/emails`,
        { search: email }
      );
    },
    getReferencePhoto(name, size = "normal") {
      return `${process.env.GOOGLE_STORAGE_URL}/${process.env.GOOGLE_STORAGE_REFERENCE_BUCKET}/${size}/${name}`;
    },
    getInspirationVideo(name) {
      return `${process.env.GOOGLE_STORAGE_URL}/${process.env.GOOGLE_STORAGE_ASSETS_BUCKET}/inspiration_videos/${name}`;
    },
    getProfilePictureThumbnailUrl(fileId, size = 256, format = "jpg") {
      return `${process.env.GOOGLE_STORAGE_URL}/${process.env.GOOGLE_STORAGE_PROFILE_CONVERTED_BUCKET}/${fileId}_${size}.${format}`;
    },
    getBlogPhotoUrl(id, size = "thumbnail") {
      return `${process.env.GOOGLE_STORAGE_URL}/${process.env.GOOGLE_STORAGE_BLOG_BUCKET}/${size}/${id}`;
    },
    getTenderPhotoUrl(id, size = "thumbnail") {
      return `${process.env.GOOGLE_STORAGE_URL}/${process.env.GOOGLE_STORAGE_TENDER_PHOTO_BUCKET}/${size}/${id}`;
    },
    noImage(e) {
      e.target.src = require("../assets/default-avatar.png");
    },
    getMainDomain() {
      const hostname = new URL(this.topDomain).hostname;
      if (hostname) {
        const parts = hostname.split(".");

        while (parts.length > 2) {
          parts.shift();
        }
        return parts.join(".");
      }
      return "";
    },
    capitalize(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    stringToSlug(str) {
      str = str.replace(/^\s+|\s+$/g, ""); // trim
      str = str.toLowerCase();

      // remove accents, swap ñ for n, etc
      const from = "àáäâèéëêìíïîòóöőôùúüűûñç·/_,:;";
      const to = "aaaaeeeeiiiiooooouuuuunc------";
      for (let i = 0, l = from.length; i < l; i++) {
        str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
      }

      str = str
        .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
        .replace(/\s+/g, "-") // collapse whitespace and replace by -
        .replace(/-+/g, "-"); // collapse dashes

      return str;
    },
    formatNumber(number) {
      return number.toString().replace(/(.)(?=(\d{3})+$)/g, "$1 ");
    },
    localeFormatNumber(number, precision, locale = "hu-HU") {
      return number.toLocaleString(locale, {
        maximumFractionDigits: precision,
      });
    },
    getPhoneNumberExtension(number) {
      if (!number) {
        return "";
      }
      const isVirtual = number.includes(",");

      if (isVirtual) {
        return number.split(",")[1];
      }
      return "";
    },
    formatPhoneNumber,
    getPhoneNumberLink,
    normalizeInput(input) {
      return input
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036F]/g, "");
    },
    isHeroku() {
      return process.env.HEROKU === "true";
    },
    isDev() {
      return process.env.DEPLOY_STAGE === "development";
    },
    isProduction() {
      return process.env.DEPLOY_STAGE === "production";
    },
    arrayRange(start, stop, step) {
      return Array.from(
        { length: (stop - start) / step + 1 },
        (_value, index) => start + index * step
      );
    },
    getFromLocalStorage(key, defaultObj = "{}") {
      return JSON.parse(localStorage.getItem(key) || defaultObj);
    },
    setToLocalStorage(key, value) {
      localStorage.setItem(key, JSON.stringify(value));
    },
    // Cookie list tools
    addPersonToCookieList(personId, cookieName) {
      const existingList = this.getFromLocalStorage(cookieName, "[]");
      const newList = [...new Set([...existingList, personId])];
      this.setToLocalStorage(cookieName, newList);
    },
    removePersonFromCookieList(personId, cookieName) {
      const existingList = this.getFromLocalStorage(cookieName, "[]");
      const newList = existingList.filter((id) => id !== personId);
      this.setToLocalStorage(cookieName, newList);
    },
    isPersonInCookieList(personId, cookieName) {
      const existingList = this.getFromLocalStorage(cookieName, "[]");
      return !!existingList.find((id) => id === personId);
    },
    getIsoString(year, month, day, hour = 0, minute = 0, second = 0) {
      return new Date(
        Date.UTC(year, month, day, hour, minute, second)
      ).toISOString();
    },
  };

  Vue.prototype.$helpers = helpers;
  inject("helpers", helpers);
}
